// src/store/slices/fscComplianceSlice.js

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getProjectContext } from '../../api/projectContext';
import {
  getFSCControls,
  getFSCControlTree,
  getFSCControl,
  createFSCControl,
  updateFSCControl,
  deleteFSCControl,
  bulkCreateFSCControls,
  getFSCAssessments,
  createFSCAssessment,
  deleteFSCAssessment,
  bulkCreateFSCAssessments,
} from '../../api/fscApi';
import { CLOUD_SCOPE_ALL, getCloudScopeConfig } from '../../pages/Cloud/cloudScopeConfig';

const withCloudScope = (payload = {}, getState) => {
  if (payload.cloud_scope) return payload;

  const platform = payload.cloud || payload.cloud_provider || payload.platform;
  if (!getCloudScopeConfig(platform).enabled) return payload;

  return {
    ...payload,
    cloud_scope: getState()?.compliance?.selectedCloudScope || CLOUD_SCOPE_ALL,
  };
};

// 取得控制項列表
export const fetchFSCControls = createAsyncThunk(
  'fscCompliance/fetchControls',
  async (params = {}, { rejectWithValue, getState }) => {
    try {
      const response = await getFSCControls(withCloudScope(params, getState));
      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || '取得控制項失敗');
    }
  }
);

// 取得樹狀控制項
export const fetchFSCControlTree = createAsyncThunk(
  'fscCompliance/fetchControlTree',
  async (params = {}, { rejectWithValue, getState }) => {
    try {
      const response = await getFSCControlTree(withCloudScope(params, getState));
      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || '取得控制項樹失敗');
    }
  }
);

// 取得單一控制項
export const fetchFSCControl = createAsyncThunk(
  'fscCompliance/fetchControl',
  async ({ controlUuid, cloud, ...rest }, { rejectWithValue, getState }) => {
    try {
      const response = await getFSCControl(controlUuid, withCloudScope({ cloud, ...rest }, getState));
      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || '取得控制項失敗');
    }
  }
);

// 新增控制項
export const addFSCControl = createAsyncThunk(
  'fscCompliance/addControl',
  async (data, { rejectWithValue, getState }) => {
    try {
      const response = await createFSCControl(withCloudScope(data, getState));
      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || '新增控制項失敗');
    }
  }
);

// 更新控制項
export const editFSCControl = createAsyncThunk(
  'fscCompliance/editControl',
  async ({ control_uuid, data }, { rejectWithValue, getState }) => {
    try {
      const response = await updateFSCControl(control_uuid, withCloudScope(data, getState));
      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || '更新控制項失敗');
    }
  }
);

// 刪除控制項
export const removeFSCControl = createAsyncThunk(
  'fscCompliance/removeControl',
  async ({ control_uuid, project_uuid, cloud, cloud_scope }, { rejectWithValue, getState }) => {
    try {
      const ctx = getProjectContext();
      const response = await deleteFSCControl(
        withCloudScope(
          {
            control_uuid,
            cloud,
            cloud_scope,
            project_uuid: project_uuid || ctx.project_uuid,
          },
          getState
        )
      );
      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || '刪除控制項失敗');
    }
  }
);

// 批次新增控制項（也可用來更新既有 control：後端 bulk_upsert_controls）
export const bulkAddFSCControls = createAsyncThunk(
  'fscCompliance/bulkAddControls',
  async (payload, { rejectWithValue, getState }) => {
    try {
      const response = await bulkCreateFSCControls(withCloudScope(payload, getState));
      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || '批次新增控制項失敗');
    }
  }
);

// 批次編輯條款（control + 當月 assessment）
// - control_name / recommendation / remediation_steps 一律寫回 control（全域）
// - status 若為 null/空字串，代表不變更當月狀態（不送 assessment）
// - assessment 以當前月份的第一天作為 assessment_date（YYYY-MM-01）
export const bulkEditFSCClauses = createAsyncThunk(
  'fscCompliance/bulkEditClauses',
  async ({ projectUuid, monthKey, drafts, cloud, cloud_scope }, { rejectWithValue, getState }) => {
    if ((drafts || []).some((draft) => draft?.leafNode?.read_only)) {
      return rejectWithValue('未分派範圍的舊條款僅供檢視，無法編輯');
    }

    try {
      const ctx = getProjectContext();
      const effectiveProjectUuid = projectUuid || ctx.project_uuid;
      const safeMonthKey = String(monthKey || '').slice(0, 7);
      const assessmentDate = safeMonthKey ? `${safeMonthKey}-01` : null;
      const scoped = withCloudScope({ cloud, cloud_scope }, getState);

      // 1) controls：用 bulkCreateFSCControls 走後端 bulk_upsert_controls（可更新既有 control）
      const controls = (drafts || []).map((d) => {
        const leaf = d?.leafNode || {};
        const edits = d?.edits || {};
        return {
          control_id: leaf.control_id,
          control_name: edits.control_name,
          recommendation: edits.recommendation ?? '',
          remediation_steps: edits.remediation_steps ?? '',
        };
      });

      // 2) assessments：只送有 status 的草稿
      const assessments = (drafts || [])
        .filter((d) => {
          const st = d?.edits?.status;
          return st === 'passed' || st === 'partial' || st === 'failed';
        })
        .map((d) => {
          const leaf = d?.leafNode || {};
          const edits = d?.edits || {};
          return {
            control_uuid: leaf.uuid || leaf.id || null,
            control_id: leaf.control_id,
            assessment_date: assessmentDate,
            status: edits.status,
          };
        });

      const tasks = [];
      tasks.push(
        bulkCreateFSCControls({
          project_uuid: effectiveProjectUuid,
          cloud: scoped.cloud,
          cloud_scope: scoped.cloud_scope,
          controls,
        })
      );
      if (assessments.length > 0) {
        tasks.push(
          bulkCreateFSCAssessments({
            project_uuid: effectiveProjectUuid,
            cloud: scoped.cloud,
            cloud_scope: scoped.cloud_scope,
            assessments,
          })
        );
      }

      const results = await Promise.all(tasks);
      return { results };
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || '批次編輯條款失敗');
    }
  }
);

// 取得 assessments
export const fetchFSCAssessments = createAsyncThunk(
  'fscCompliance/fetchAssessments',
  async (params = {}, { rejectWithValue, getState }) => {
    try {
      const response = await getFSCAssessments(withCloudScope(params, getState));
      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || '取得評估資料失敗');
    }
  }
);

// 儲存 assessment（單筆）
export const saveFSCAssessment = createAsyncThunk(
  'fscCompliance/saveAssessment',
  async (data, { rejectWithValue, getState }) => {
    try {
      const response = await createFSCAssessment(withCloudScope(data, getState));
      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || '儲存評估資料失敗');
    }
  }
);

// 刪除 assessment
export const removeFSCAssessment = createAsyncThunk(
  'fscCompliance/removeAssessment',
  async ({ assessmentUuid, cloud, ...rest }, { rejectWithValue, getState }) => {
    try {
      const response = await deleteFSCAssessment(assessmentUuid, withCloudScope({ cloud, ...rest }, getState));
      return response;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || '刪除評估資料失敗');
    }
  }
);

const initialState = {
  controls: [],
  controlTree: [],
  assessments: [],
  controlsLoading: false,
  assessmentsLoading: false,
  saving: false,
  error: null,

  showControlModal: false,
  editingControl: null,

  showAssessmentModal: false,
  editingAssessment: null,

  showDeleteConfirm: false,
};

const fscComplianceSlice = createSlice({
  name: 'fscCompliance',
  initialState,
  reducers: {
    clearError(state) {
      state.error = null;
    },
    setEditingControl(state, action) {
      state.editingControl = action.payload;
    },
    setEditingAssessment(state, action) {
      state.editingAssessment = action.payload;
    },
    openControlModal(state) {
      state.showControlModal = true;
    },
    closeControlModal(state) {
      state.showControlModal = false;
      state.editingControl = null;
    },
    openAssessmentModal(state) {
      state.showAssessmentModal = true;
    },
    closeAssessmentModal(state) {
      state.showAssessmentModal = false;
      state.editingAssessment = null;
    },
    openDeleteConfirm(state) {
      state.showDeleteConfirm = true;
    },
    closeDeleteConfirm(state) {
      state.showDeleteConfirm = false;
    },
    resetFSCState() {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchFSCControls.pending, (state) => {
        state.controlsLoading = true;
        state.error = null;
      })
      .addCase(fetchFSCControls.fulfilled, (state, action) => {
        state.controlsLoading = false;
        state.controls = action.payload.controls || [];
      })
      .addCase(fetchFSCControls.rejected, (state, action) => {
        state.controlsLoading = false;
        state.error = action.payload;
      })

      .addCase(fetchFSCControlTree.pending, (state) => {
        state.controlsLoading = true;
        state.error = null;
      })
      .addCase(fetchFSCControlTree.fulfilled, (state, action) => {
        state.controlsLoading = false;
        state.controlTree = action.payload.tree || [];
      })
      .addCase(fetchFSCControlTree.rejected, (state, action) => {
        state.controlsLoading = false;
        state.error = action.payload;
      })

      .addCase(addFSCControl.pending, (state) => {
        state.saving = true;
      })
      .addCase(addFSCControl.fulfilled, (state) => {
        state.saving = false;
        state.showControlModal = false;
        state.editingControl = null;
      })
      .addCase(addFSCControl.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload;
      })

      .addCase(editFSCControl.pending, (state) => {
        state.saving = true;
      })
      .addCase(editFSCControl.fulfilled, (state) => {
        state.saving = false;
        state.showControlModal = false;
        state.editingControl = null;
      })
      .addCase(editFSCControl.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload;
      })

      .addCase(removeFSCControl.pending, (state) => {
        state.saving = true;
      })
      .addCase(removeFSCControl.fulfilled, (state) => {
        state.saving = false;
        state.showDeleteConfirm = false;
      })
      .addCase(removeFSCControl.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload;
      })

      .addCase(fetchFSCAssessments.pending, (state) => {
        state.assessmentsLoading = true;
        state.error = null;
      })
      .addCase(fetchFSCAssessments.fulfilled, (state, action) => {
        state.assessmentsLoading = false;
        state.assessments = action.payload.assessments || [];
      })
      .addCase(fetchFSCAssessments.rejected, (state, action) => {
        state.assessmentsLoading = false;
        state.error = action.payload;
      })

      .addCase(saveFSCAssessment.pending, (state) => {
        state.saving = true;
      })
      .addCase(saveFSCAssessment.fulfilled, (state) => {
        state.saving = false;
        state.showAssessmentModal = false;
        state.editingAssessment = null;
      })
      .addCase(saveFSCAssessment.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload;
      })

      .addCase(bulkEditFSCClauses.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(bulkEditFSCClauses.fulfilled, (state) => {
        state.saving = false;
      })
      .addCase(bulkEditFSCClauses.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload;
      });
  },
});

export const {
  clearError,
  setEditingControl,
  setEditingAssessment,
  openControlModal,
  closeControlModal,
  openAssessmentModal,
  closeAssessmentModal,
  openDeleteConfirm,
  closeDeleteConfirm,
  resetFSCState,
} = fscComplianceSlice.actions;

export default fscComplianceSlice.reducer;
